import { useMemo, useState, useEffect, useLayoutEffect } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { FilterToggle, FiltersPanel } from '@components/filters';
import { getProjects, getProjectDisplayName, type Project } from '@data/projects';
import { useAuth } from '@services/auth';
import { useAdminViewAs } from '../../lib/adminViewAs';
import { getStoredCompanyScopeMode, COMPANY_WORKSPACE_CHANGED_EVENT } from '@services/companies';
import {
	buildGardenerProjectsSessionKey,
	readGardenerProjectsSession,
	writeGardenerProjectsSession,
	readPlanThumbSession,
	writePlanThumbSession
} from '@services/projectsSessionCache';
import { getPlanById } from '@services/plans';
import { getImageUrl } from '@utils/imageUrl';
import CompanyWorkspaceStrip from '../../shared/CompanyWorkspaceStrip';
import SentOffers from './SentOffers';
import { useI18n } from '../../i18n';

const STATUSES = ['planning', 'quoted', 'in_progress', 'completed', 'cancelled'] as const;

const PLAN_THUMB_FALLBACK =
	'https://images.unsplash.com/photo-1416879595882-3373a0480b5b?w=800&h=400&fit=crop';

type TabId = 'projects' | 'offers';

type ListScope = 'active' | 'archive' | 'all';

function projectMatchesListScope(project: Project, scope: ListScope): boolean {
	if (scope === 'all') return true;
	if (scope === 'active') {
		return project.status !== 'completed' && project.status !== 'cancelled';
	}
	return project.status === 'completed' || project.status === 'cancelled';
}

function isDueDatePastCalendar(due?: string): boolean {
	if (!due) return false;
	const d = new Date(due);
	if (!Number.isFinite(d.getTime())) return false;
	const end = new Date(d);
	end.setHours(23, 59, 59, 999);
	return end.getTime() < Date.now();
}

export default function Projects() {
	const navigate = useNavigate();
	const { language } = useI18n();
	const copy = (en: string, de: string, hr: string) =>
		language === 'de' ? de : language === 'hr' ? hr : en;
	const locale = language === 'de' ? 'de-DE' : language === 'hr' ? 'hr-HR' : 'en-GB';
	const labelProjectStatus = (status: string) => {
		if (status === 'planning') return copy('planning', 'Planung', 'planiranje');
		if (status === 'quoted') return copy('quoted', 'angeboten', 'ponuđeno');
		if (status === 'in_progress') return copy('in progress', 'in Arbeit', 'u tijeku');
		if (status === 'completed') return copy('completed', 'abgeschlossen', 'završeno');
		if (status === 'cancelled') return copy('cancelled', 'abgebrochen', 'otkazano');
		return status.replace('_', ' ');
	};
	const labelPriority = (priority: 'low' | 'medium' | 'high') => {
		if (priority === 'low') return copy('low', 'niedrig', 'niska');
		if (priority === 'medium') return copy('medium', 'mittel', 'srednja');
		return copy('high', 'hoch', 'visoka');
	};
	const [searchParams, setSearchParams] = useSearchParams();
	const { user } = useAuth();
	const { viewAsTarget } = useAdminViewAs();
	const tab = (searchParams.get('tab') === 'offers' ? 'offers' : 'projects') as TabId;
	const [projects, setProjects] = useState<Project[]>([]);
	const [isLoading, setIsLoading] = useState(true);
	const [searchTerm, setSearchTerm] = useState('');
	const [selectedStatuses, setSelectedStatuses] = useState<string[]>([]);
	const [selectedPriorities, setSelectedPriorities] = useState<Array<'low' | 'medium' | 'high'>>([]);
	const [budgetMin, setBudgetMin] = useState('');
	const [budgetMax, setBudgetMax] = useState('');
	const [selectedTags, setSelectedTags] = useState<string[]>([]);
	const [isFiltersOpen, setIsFiltersOpen] = useState(false);
	const [workspaceListRev, setWorkspaceListRev] = useState(0);
	const [listScope, setListScope] = useState<ListScope>('active');
	const [planImageByPlanId, setPlanImageByPlanId] = useState<Record<number, string>>({});

	const gardenerSessionKey = useMemo(() => {
		if (!user) return null;
		const companyScope = getStoredCompanyScopeMode() === 'all_companies' ? 'all_companies' : 'company';
		const adminAs =
			user.role === 'admin' && viewAsTarget
				? `${viewAsTarget.role}:${viewAsTarget.id}:${companyScope}`
				: null;
		return buildGardenerProjectsSessionKey(user, companyScope, adminAs);
	}, [user, workspaceListRev, user?.activeCompanyId, viewAsTarget]);

	useEffect(() => {
		const onWorkspace = () => setWorkspaceListRev((n) => n + 1);
		window.addEventListener(COMPANY_WORKSPACE_CHANGED_EVENT, onWorkspace);
		return () => window.removeEventListener(COMPANY_WORKSPACE_CHANGED_EVENT, onWorkspace);
	}, []);

	// Restore list from in-memory session before paint so revisiting the page does not flash empty/loading.
	useLayoutEffect(() => {
		if (!user) {
			setProjects([]);
			setIsLoading(false);
			return;
		}
		if (!gardenerSessionKey) {
			setIsLoading(true);
			return;
		}
		const cached = readGardenerProjectsSession(gardenerSessionKey);
		if (cached !== undefined) {
			setProjects(cached);
			setIsLoading(false);
		} else {
			setIsLoading(true);
		}
	}, [user, gardenerSessionKey]);

	// Fetch in background when we already have a cache; show spinner only on first load for this workspace key.
	useEffect(() => {
		if (!user || !gardenerSessionKey) {
			if (!user) {
				setProjects([]);
				setIsLoading(false);
			}
			return;
		}

		let cancelled = false;

		const fetchList = async (): Promise<Project[]> => {
			const showAllCompanies = getStoredCompanyScopeMode() === 'all_companies';
			if (user.role === 'admin' && viewAsTarget?.role === 'seeker') {
				return (await getProjects({ seeker_user_id: viewAsTarget.id })) || [];
			}
			if (
				user.role === 'admin' &&
				viewAsTarget &&
				(viewAsTarget.role === 'gardener' || viewAsTarget.role === 'company')
			) {
				const rid = viewAsTarget.id;
				return (
					(await getProjects({
						requester_user_id: rid,
						company_scope: showAllCompanies ? 'all_companies' : 'company'
					})) || []
				);
			}
			if (user.role === 'admin') {
				return (await getProjects()) || [];
			}
			if (user.role === 'gardener' || user.role === 'company') {
				const parsedId = parseInt(user.id, 10);
				if (!Number.isNaN(parsedId)) {
					return (
						(await getProjects({
							requester_user_id: parsedId,
							company_scope: showAllCompanies ? 'all_companies' : 'company'
						})) || []
					);
				}
				console.warn('User ID is not a valid number:', user.id);
				return [];
			}
			return [];
		};

		const run = async (showSpinner: boolean) => {
			if (showSpinner) setIsLoading(true);
			try {
				const loaded = await fetchList();
				if (cancelled) return;
				setProjects(loaded);
				writeGardenerProjectsSession(gardenerSessionKey, loaded);
			} catch (err) {
				console.error('Failed to load projects:', err);
				if (!cancelled) setProjects([]);
			} finally {
				if (!cancelled && showSpinner) setIsLoading(false);
			}
		};

		const hadCache = readGardenerProjectsSession(gardenerSessionKey) !== undefined;
		void run(!hadCache);

		const handleProjectsUpdate = async () => {
			if (cancelled) return;
			try {
				const loaded = await fetchList();
				if (cancelled) return;
				setProjects(loaded);
				writeGardenerProjectsSession(gardenerSessionKey, loaded);
			} catch (err) {
				console.error('Failed to reload projects:', err);
			}
		};
		window.addEventListener('projectsUpdated', handleProjectsUpdate);

		return () => {
			cancelled = true;
			window.removeEventListener('projectsUpdated', handleProjectsUpdate);
		};
	}, [user, gardenerSessionKey]);

	const allTags = useMemo(
		() => Array.from(new Set((projects || []).flatMap((project) => project.tags || []))).sort(),
		[projects]
	);

	useEffect(() => {
		if (!projects.length) return;
		let cancelled = false;
		const uniquePlanIds = [
			...new Set(
				projects
					.map((p) => (p.planId ? Number(p.planId) : NaN))
					.filter((n) => !Number.isNaN(n))
			)
		];
		if (!uniquePlanIds.length) return;

		const fromCache: Record<number, string> = {};
		const missing: number[] = [];
		for (const id of uniquePlanIds) {
			const hit = readPlanThumbSession(id);
			if (hit) fromCache[id] = hit;
			else missing.push(id);
		}
		if (Object.keys(fromCache).length > 0) {
			setPlanImageByPlanId((prev) => ({ ...fromCache, ...prev }));
		}
		if (!missing.length) return;

		(async () => {
			const entries = await Promise.all(
				missing.map(async (planId) => {
					try {
						const pl = await getPlanById(planId);
						const raw =
							pl.after_image_url && !pl.after_image_url.includes('unsplash.com')
								? pl.after_image_url
								: pl.before_image_url || PLAN_THUMB_FALLBACK;
						const url = getImageUrl(raw) || raw;
						return [planId, url] as const;
					} catch {
						return [planId, PLAN_THUMB_FALLBACK] as const;
					}
				})
			);
			if (cancelled) return;
			for (const [id, url] of entries) writePlanThumbSession(id, url);
			setPlanImageByPlanId((prev) => {
				const next = { ...prev };
				for (const [id, url] of entries) next[id] = url;
				return next;
			});
		})();

		return () => {
			cancelled = true;
		};
	}, [projects]);

	const scopeCounts = useMemo(() => {
		let active = 0;
		let archive = 0;
		for (const p of projects) {
			if (projectMatchesListScope(p, 'active')) active += 1;
			if (projectMatchesListScope(p, 'archive')) archive += 1;
		}
		return { active, archive, total: projects.length };
	}, [projects]);

	const filteredProjects = useMemo(() => {
		if (!Array.isArray(projects)) return [];
		const minBudget = budgetMin ? Number(budgetMin) : undefined;
		const maxBudget = budgetMax ? Number(budgetMax) : undefined;
		const normalizedSearch = searchTerm.trim().toLowerCase();

		return projects.filter((project) => {
			if (!project) return false;

			const projectBudget =
				typeof project.budget === 'string'
					? parseFloat(project.budget)
					: typeof project.budget === 'number'
						? project.budget
						: 0;

			if (!projectMatchesListScope(project, listScope)) return false;

			const matchesStatus =
				selectedStatuses.length === 0 || selectedStatuses.includes(project.status);

			const matchesPriority =
				selectedPriorities.length === 0 ||
				(project.priority && selectedPriorities.includes(project.priority));

			const matchesTags =
				selectedTags.length === 0 ||
				(project.tags &&
					Array.isArray(project.tags) &&
					selectedTags.every((tag) =>
						project.tags!.map((t) => String(t).toLowerCase()).includes(tag.toLowerCase())
					));

			const matchesBudget =
				(minBudget === undefined || projectBudget >= minBudget) &&
				(maxBudget === undefined || projectBudget <= maxBudget);

			const matchesSearch =
				!normalizedSearch ||
				getProjectDisplayName(project).toLowerCase().includes(normalizedSearch) ||
				(project.client && project.client.toLowerCase().includes(normalizedSearch)) ||
				(project.owner || '').toLowerCase().includes(normalizedSearch) ||
				(project.tags &&
					Array.isArray(project.tags) &&
					project.tags.some((tag) => String(tag).toLowerCase().includes(normalizedSearch)));

			return matchesStatus && matchesPriority && matchesTags && matchesBudget && matchesSearch;
		});
	}, [
		projects,
		listScope,
		searchTerm,
		selectedStatuses,
		selectedPriorities,
		selectedTags,
		budgetMin,
		budgetMax
	]);

	const displayProjects = useMemo(() => {
		const list = [...filteredProjects];
		if (listScope === 'archive') {
			list.sort(
				(a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()
			);
		} else {
			list.sort((a, b) => {
				const da = a.dueDate ? new Date(a.dueDate).getTime() : Number.POSITIVE_INFINITY;
				const db = b.dueDate ? new Date(b.dueDate).getTime() : Number.POSITIVE_INFINITY;
				if (da !== db) return da - db;
				return getProjectDisplayName(a).localeCompare(getProjectDisplayName(b));
			});
		}
		return list;
	}, [filteredProjects, listScope]);

	const activeFiltersCount = useMemo(() => {
		let count = 0;
		count += selectedStatuses.length;
		count += selectedPriorities.length;
		count += selectedTags.length;
		if (budgetMin) count += 1;
		if (budgetMax) count += 1;
		return count;
	}, [selectedStatuses, selectedPriorities, selectedTags, budgetMin, budgetMax]);

	const toggleSelection = <T,>(value: T, collection: T[], setCollection: (items: T[]) => void) => {
		setCollection(
			collection.includes(value)
				? collection.filter((item) => item !== value)
				: [...collection, value]
		);
	};

	const handleResetFilters = () => {
		setSelectedStatuses([]);
		setSelectedPriorities([]);
		setSelectedTags([]);
		setBudgetMin('');
		setBudgetMax('');
	};

	const setTab = (t: TabId) => {
		if (t === 'offers') setSearchParams({ tab: 'offers' });
		else setSearchParams({});
	};

	return (
		<>
			<CompanyWorkspaceStrip />
			<div className="d-flex flex-wrap align-items-center justify-content-between gap-3 mb-3">
				<h1 className="h3 mb-0">{copy('Projects', 'Projekte', 'Projekti')}</h1>
			</div>

			<ul className="nav nav-tabs nav-tabs-garden mb-3" role="tablist">
				<li className="nav-item" role="presentation">
					<button
						type="button"
						className={`nav-link ${tab === 'projects' ? 'active' : ''}`}
						onClick={() => setTab('projects')}
						aria-selected={tab === 'projects'}
					>
						<i className="bi bi-kanban me-1" aria-hidden />
						{copy('Projects', 'Projekte', 'Projekti')}
					</button>
				</li>
				<li className="nav-item" role="presentation">
					<button
						type="button"
						className={`nav-link ${tab === 'offers' ? 'active' : ''}`}
						onClick={() => setTab('offers')}
						aria-selected={tab === 'offers'}
					>
						<i className="bi bi-send-check me-1" aria-hidden />
						{copy('Sent offers', 'Gesendete Angebote', 'Poslane ponude')}
					</button>
				</li>
			</ul>

			{tab === 'offers' ? (
				<SentOffers embedded />
			) : (
				<>
			<div className="d-flex flex-wrap align-items-center justify-content-between gap-3 mb-3">
				<div
					className="btn-group flex-wrap"
					role="group"
					aria-label={copy('Project timeframe', 'Projektzeitraum', 'Razdoblje projekta')}
				>
					<button
						type="button"
						className={`btn btn-sm ${listScope === 'active' ? 'btn-success' : 'btn-outline-secondary'}`}
						onClick={() => setListScope('active')}
					>
						<i className="bi bi-play-circle me-1" aria-hidden />
						{copy('Current', 'Aktuell', 'Trenutno')}
						<span className="badge bg-light text-dark ms-1">{scopeCounts.active}</span>
					</button>
					<button
						type="button"
						className={`btn btn-sm ${listScope === 'archive' ? 'btn-success' : 'btn-outline-secondary'}`}
						onClick={() => setListScope('archive')}
					>
						<i className="bi bi-archive me-1" aria-hidden />
						{copy('Past', 'Vergangen', 'Prošlo')}
						<span className="badge bg-light text-dark ms-1">{scopeCounts.archive}</span>
					</button>
					<button
						type="button"
						className={`btn btn-sm ${listScope === 'all' ? 'btn-success' : 'btn-outline-secondary'}`}
						onClick={() => setListScope('all')}
					>
						<i className="bi bi-list-ul me-1" aria-hidden />
						{copy('All', 'Alle', 'Sve')}
						<span className="badge bg-light text-dark ms-1">{scopeCounts.total}</span>
					</button>
				</div>
				<div className="d-flex flex-wrap gap-2 justify-content-end flex-grow-1">
					<input
						type="search"
						className="form-control"
						style={{ minWidth: '200px', maxWidth: '320px' }}
						placeholder={copy('Search projects or clients...', 'Projekte oder Kunden suchen...', 'Pretraži projekte ili klijente...')}
						value={searchTerm}
						onChange={(event) => setSearchTerm(event.target.value)}
					/>
					<FilterToggle
						isOpen={isFiltersOpen}
						onToggle={() => setIsFiltersOpen((prev) => !prev)}
						activeCount={activeFiltersCount}
					/>
				</div>
			</div>
			<p className="text-muted small mb-3">
				{listScope === 'active'
					? copy(
							'Showing projects that are still open. Switch to Past for completed work.',
							'Es werden nur laufende Projekte angezeigt. Wechseln Sie zu „Vergangen“ für abgeschlossene Arbeiten.',
							'Prikazuju se samo aktivni projekti. Za završene radove odaberite „Prošlo“.'
						)
					: listScope === 'archive'
						? copy(
								'Completed or cancelled projects. Open Current to focus on what needs doing now.',
								'Abgeschlossene oder stornierte Projekte. Unter „Aktuell“ sehen Sie, was jetzt ansteht.',
								'Završeni ili otkazani projekti. „Trenutno“ pokazuje što je na redu.'
							)
						: copy(
								'All projects in your workspace.',
								'Alle Projekte in Ihrem Arbeitsbereich.',
								'Svi projekti u vašem radnom prostoru.'
							)}
			</p>
			<FiltersPanel
				isOpen={isFiltersOpen}
				onApply={() => setIsFiltersOpen(false)}
				onReset={handleResetFilters}
			>
				<div className="row g-4">
					<div className="col-12 col-lg-4">
						<div className="mb-2 text-muted text-uppercase small">{copy('Status', 'Status', 'Status')}</div>
						{STATUSES.map((value) => (
							<div className="form-check" key={value}>
								<input
									className="form-check-input"
									type="checkbox"
									value={value}
									id={`projects-status-${value}`}
									checked={selectedStatuses.includes(value)}
									onChange={() => toggleSelection(value, selectedStatuses, setSelectedStatuses)}
								/>
								<label className="form-check-label" htmlFor={`projects-status-${value}`}>
									{labelProjectStatus(value)}
								</label>
							</div>
						))}
					</div>
					<div className="col-12 col-lg-4">
						<div className="mb-2 text-muted text-uppercase small">{copy('Priority', 'Priorität', 'Prioritet')}</div>
						{(['low', 'medium', 'high'] as const).map((value) => (
							<div className="form-check" key={value}>
								<input
									className="form-check-input"
									type="checkbox"
									value={value}
									id={`projects-priority-${value}`}
									checked={selectedPriorities.includes(value)}
									onChange={() => toggleSelection(value, selectedPriorities, setSelectedPriorities)}
								/>
								<label className="form-check-label" htmlFor={`projects-priority-${value}`}>
									{labelPriority(value)}
								</label>
							</div>
						))}
					</div>
					<div className="col-12 col-lg-4">
						<div className="mb-2 text-muted text-uppercase small">{copy('Budget (€)', 'Budget (€)', 'Budžet (€)')}</div>
						<div className="input-group">
							<span className="input-group-text">{copy('Min', 'Min', 'Min')}</span>
							<input
								type="number"
								className="form-control"
								min={0}
								value={budgetMin}
								onChange={(event) => setBudgetMin(event.target.value)}
								placeholder="0"
							/>
							<span className="input-group-text">Max</span>
							<input
								type="number"
								className="form-control"
								min={0}
								value={budgetMax}
								onChange={(event) => setBudgetMax(event.target.value)}
								placeholder={copy('Any', 'Beliebig', 'Bilo koji')}
							/>
						</div>
					</div>
					<div className="col-12">
						<div className="mb-2 text-muted text-uppercase small">{copy('Tags', 'Tags', 'Oznake')}</div>
						<div className="d-flex flex-wrap gap-2">
							{allTags.map((tag) => {
								const isSelected = selectedTags.includes(tag);
								return (
									<button
										key={tag}
										type="button"
										className={`btn btn-sm ${isSelected ? 'btn-success' : 'btn-outline-secondary'}`}
										onClick={() => toggleSelection(tag, selectedTags, setSelectedTags)}
									>
										{tag}
									</button>
								);
							})}
						</div>
					</div>
				</div>
			</FiltersPanel>

			{isLoading ? (
				<div className="card">
					<div className="card-body text-center py-5">
						<div className="spinner-border text-success" role="status">
							<span className="visually-hidden">{copy('Loading...', 'Lädt...', 'Učitavanje...')}</span>
						</div>
						<p className="text-muted mt-3 mb-0">{copy('Loading projects...', 'Projekte werden geladen...', 'Učitavanje projekata...')}</p>
					</div>
				</div>
			) : projects.length === 0 ? (
				<div className="card border-0 shadow-sm">
					<div className="card-body text-center py-5">
						<i className="bi bi-folder2 text-muted" style={{ fontSize: '3rem' }} aria-hidden></i>
						<h5 className="mt-3 mb-2">{copy('No projects yet', 'Noch keine Projekte', 'Još nema projekata')}</h5>
						<p className="text-muted mb-4">
							{copy(
								'Projects appear here when a seeker accepts your offer. Send offers from the Marketplace to get started.',
								'Projekte erscheinen hier, wenn ein Gartensuchender Ihr Angebot annimmt. Senden Sie Angebote aus dem Marktplatz.',
								'Projekti će se pojaviti ovdje kada vlasnik vrta prihvati vašu ponudu. Pošaljite ponude iz tržnice.'
							)}
						</p>
						<Link to="/app/projects?tab=offers" className="btn btn-success me-2">
							{copy('View sent offers', 'Gesendete Angebote ansehen', 'Prikaži poslane ponude')}
						</Link>
						<Link to="/app/plans" className="btn btn-outline-success">
							{copy('Browse plans & send an offer', 'Pläne durchsuchen & Angebot senden', 'Pregledaj planove i pošalji ponudu')}
						</Link>
						{import.meta.env.DEV && user?.id && (
							<p className="text-muted small mt-4 mb-0">
								Dev: seed sample projects —{' '}
								<a href={`http://localhost:8080/api/seed-projects?user_id=${user.id}`} target="_blank" rel="noopener noreferrer" className="text-muted">
									/api/seed-projects?user_id={user.id}
								</a>
							</p>
						)}
					</div>
				</div>
			) : displayProjects.length === 0 ? (
				<div className="card border-0 shadow-sm">
					<div className="card-body text-center py-5">
						<i className="bi bi-funnel text-muted" style={{ fontSize: '2.5rem' }} aria-hidden />
						<p className="text-muted mt-3 mb-0">
							{listScope === 'active' && scopeCounts.active === 0
								? copy(
										'No open projects right now. Past shows completed work.',
										'Derzeit keine offenen Projekte. Unter „Vergangen“ sehen Sie abgeschlossene Arbeiten.',
										'Trenutno nema otvorenih projekata. „Prošlo“ prikazuje završene poslove.'
									)
								: copy(
										'No projects match your search or filters.',
										'Keine Projekte entsprechen Ihrer Suche oder Ihren Filtern.',
										'Nijedan projekt ne odgovara pretrazi ili filterima.'
									)}
						</p>
						{listScope === 'active' && scopeCounts.archive > 0 && (
							<button
								type="button"
								className="btn btn-outline-success btn-sm mt-3"
								onClick={() => setListScope('archive')}
							>
								{copy('View past projects', 'Vergangene Projekte anzeigen', 'Prikaži prošle projekte')}
							</button>
						)}
					</div>
				</div>
			) : (
				<div className="row g-4">
					{displayProjects.map((project) => {
						const planIdNum = project.planId ? Number(project.planId) : NaN;
						const thumb =
							!Number.isNaN(planIdNum) && planImageByPlanId[planIdNum]
								? planImageByPlanId[planIdNum]
								: PLAN_THUMB_FALLBACK;
						const overdue =
							isDueDatePastCalendar(project.dueDate) &&
							project.status !== 'completed' &&
							project.status !== 'cancelled';
						return (
							<div key={project.id} className="col-12 col-lg-6">
								<div
									role="link"
									tabIndex={0}
									className="card border-0 shadow-sm overflow-hidden plan-card h-100"
									style={{ cursor: 'pointer' }}
									onClick={() => navigate(`/app/gardener/project/${project.id}`)}
									onKeyDown={(e) => {
										if (e.key === 'Enter' || e.key === ' ') {
											e.preventDefault();
											navigate(`/app/gardener/project/${project.id}`);
										}
									}}
								>
									<div className="row g-0">
										<div className="col-12 col-sm-4 col-lg-5">
											<div className="ratio ratio-4x3 h-100" style={{ minHeight: '140px' }}>
												<img
													src={thumb}
													alt=""
													className="object-fit-cover w-100 h-100"
													style={{ objectFit: 'cover' }}
													loading="lazy"
												/>
											</div>
										</div>
										<div className="col-12 col-sm-8 col-lg-7">
											<div className="card-body p-3 p-md-4 d-flex flex-column h-100">
												<div className="d-flex flex-wrap align-items-start gap-2 mb-2">
													<span
														className={`badge ${
															project.status === 'completed'
																? 'text-bg-success'
																: project.status === 'cancelled'
																	? 'text-bg-secondary'
																	: project.status === 'in_progress'
																		? 'text-bg-primary'
																		: project.status === 'quoted'
																			? 'text-bg-info'
																			: 'text-bg-secondary'
														}`}
													>
														{labelProjectStatus(project.status)}
													</span>
													{overdue && (
														<span className="badge text-bg-danger">
															{copy('Overdue', 'Überfällig', 'Prekoračeno')}
														</span>
													)}
													<span
														className={`badge ${
															project.priority === 'high'
																? 'text-bg-danger'
																: project.priority === 'medium'
																	? 'text-bg-warning'
																	: 'text-bg-info'
														}`}
													>
														{labelPriority(project.priority)}
													</span>
													{project.offerId && (
														<span className="badge text-bg-light text-success border">
															{copy('From offer', 'Aus Angebot', 'Iz ponude')}
														</span>
													)}
												</div>
												<h2 className="h5 mb-2 fw-bold">{getProjectDisplayName(project)}</h2>
												<div className="text-muted small mb-2">
													{project.client && (
														<div>
															<i className="bi bi-person me-1" aria-hidden />
															{project.client}
														</div>
													)}
													{(project.owner || project.gardenerName) && (
														<div>
															<i className="bi bi-person-badge me-1" aria-hidden />
															{project.owner || project.gardenerName}
														</div>
													)}
													<div>
														<i className="bi bi-currency-euro me-1" aria-hidden />
														{project.budget.toLocaleString()} €
														{project.dueDate && (
															<>
																{' · '}
																<i className="bi bi-calendar3 me-1" aria-hidden />
																{copy('Due', 'Fällig', 'Rok')}{' '}
																{new Date(project.dueDate).toLocaleDateString(locale)}
															</>
														)}
													</div>
												</div>
												{(project.tags?.length ?? 0) > 0 && (
													<div className="d-flex flex-wrap gap-1 mb-3">
														{(project.tags || []).slice(0, 4).map((tag) => (
															<span key={tag} className="badge text-bg-light text-muted">
																{tag}
															</span>
														))}
														{(project.tags || []).length > 4 && (
															<span className="badge text-bg-light text-muted">
																+{(project.tags || []).length - 4}
															</span>
														)}
													</div>
												)}
												<div className="mt-auto pt-1">
													<span className="btn btn-success btn-sm">
														<i className="bi bi-arrow-right-circle me-1" aria-hidden />
														{copy('Open project', 'Projekt öffnen', 'Otvori projekt')}
													</span>
													<span className="text-muted small ms-2">
														{copy('Updated', 'Aktualisiert', 'Ažurirano')}{' '}
														{new Date(project.updatedAt).toLocaleDateString(locale)}
													</span>
												</div>
											</div>
										</div>
									</div>
								</div>
							</div>
						);
					})}
				</div>
			)}
				</>
			)}
		</>
	);
}
